import { useEffect, useRef, useState } from 'react';
import { Bookmark, Check, Trash2 } from 'lucide-react';
import './SavedFiltersMenu.scss';

export interface SavedFilterOption {
  id: string;
  name: string;
}

interface SavedFiltersMenuProps {
  savedFilters: SavedFilterOption[];
  /** The saved filter matching the current filters, or '' when none. */
  activeFilterId: string;
  onApply: (id: string) => void;
  onDelete: (id: string) => void | Promise<void>;
  onSaveNew: (name: string) => void | Promise<void>;
  onUpdate: (id: string) => void | Promise<void>;
  isSaving?: boolean;
  isDeleting?: boolean;
}

/**
 * Saved-filter menu for the orders table. One trigger button opens a panel that
 * applies, saves, updates and deletes views — replacing the row of controls
 * whose naming input kept reading as the orders search bar. Deleting is a
 * two-step inline confirm so a stray double-click can't wipe a view.
 */
export function SavedFiltersMenu({
  savedFilters,
  activeFilterId,
  onApply,
  onDelete,
  onSaveNew,
  onUpdate,
  isSaving = false,
  isDeleting = false,
}: SavedFiltersMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [confirmingDeleteId, setConfirmingDeleteId] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
        setConfirmingDeleteId('');
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
  }, [isOpen]);

  const activeFilter = savedFilters.find((f) => f.id === activeFilterId) ?? null;

  const handleSaveNew = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await onSaveNew(trimmed);
    setName('');
  };

  return (
    <div className="saved-filters" ref={ref}>
      <button
        type="button"
        className="btn btn--ghost saved-filters__trigger"
        onClick={() => setIsOpen((o) => !o)}
        aria-expanded={isOpen}
        title="Saved filters"
      >
        <Bookmark size={15} />
        Saved filters
        {activeFilter && <span className="saved-filters__active-pill">{activeFilter.name}</span>}
      </button>

      {isOpen && (
        <div className="saved-filters__panel">
          <div className="saved-filters__header">
            <span className="saved-filters__title">Saved filters</span>
            <button
              className="saved-filters__close"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="saved-filters__list">
            {savedFilters.length === 0 ? (
              <div className="saved-filters__empty">No saved filters yet.</div>
            ) : (
              savedFilters.map((f) =>
                confirmingDeleteId === f.id ? (
                  <div key={f.id} className="saved-filters__confirm">
                    <span>
                      Delete “<strong>{f.name}</strong>”?
                    </span>
                    <div className="saved-filters__confirm-actions">
                      <button
                        className="btn btn--sm saved-filters__confirm-delete"
                        disabled={isDeleting}
                        onClick={async () => {
                          await onDelete(f.id);
                          setConfirmingDeleteId('');
                        }}
                      >
                        Delete
                      </button>
                      <button
                        className="btn btn--sm btn--ghost"
                        onClick={() => setConfirmingDeleteId('')}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    key={f.id}
                    className={`saved-filters__row${
                      f.id === activeFilterId ? ' saved-filters__row--active' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="saved-filters__apply"
                      onClick={() => {
                        onApply(f.id);
                        setIsOpen(false);
                      }}
                      title={`Apply “${f.name}”`}
                    >
                      <span className="saved-filters__check">
                        {f.id === activeFilterId && <Check size={14} />}
                      </span>
                      <span className="saved-filters__name">{f.name}</span>
                    </button>
                    <button
                      type="button"
                      className="saved-filters__delete"
                      onClick={() => setConfirmingDeleteId(f.id)}
                      aria-label={`Delete ${f.name}`}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )
              )
            )}
          </div>

          <div className="saved-filters__footer">
            {activeFilter && (
              <button
                type="button"
                className="btn btn--ghost btn--sm saved-filters__update"
                disabled={isSaving}
                onClick={() => onUpdate(activeFilter.id)}
              >
                Update “{activeFilter.name}” with current filters
              </button>
            )}

            <label className="saved-filters__save-label">Save current filters as</label>
            <div className="saved-filters__save-row">
              <input
                className="saved-filters__name-input"
                placeholder="e.g. NY invoiced"
                value={name}
                maxLength={60}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleSaveNew();
                }}
              />
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={!name.trim() || isSaving}
                onClick={() => void handleSaveNew()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SavedFiltersMenu;
